import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useGetSearchProgramsForProjectMutation } from "../../../../../../api/services/project/projectApis";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../../../components";
import { ButtonWithIcon } from "../../../../../../components/buttonWithIcon/ButtonWithIcon";
import { PAGE_URLS } from "../../../../../../helpers/constants";
import { getValueOrBlank, isNotEmpty } from "../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../redux/slices/appState/appStateSlice";
import { keyDetailsSelector } from "../../../../../../redux/slices/project/projectSelector";
import { startChildProgramNavigation } from "../../../../../../redux/slices/project/projectSlice";
import { ChildProgramsListRowValue } from "../../../../modelsAndSchemas/Models";
import { childProgramsSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ChildProgramsWrapper } from "./ChildProgramsMain.style";
import { AddExistingPrograms } from "./components/addExistingPrograms/AddExistingPrograms";
import { ChildProgramsList } from "./components/childProgramsList/ChildProgramsList";

interface ChildProgramsMainProps {
  isInViewMode?: boolean;
  projectId?: string;
  isEditProject?: boolean;
  pageUrl: string;
  isNavigatedFromProgram?: boolean;
}

export const ChildProgramsMain: React.FC<ChildProgramsMainProps> = ({
  isInViewMode = false,
  projectId,
  isEditProject,
  pageUrl,
  isNavigatedFromProgram,
}) => {
  const { useFetchLabel, commonLabels, fetchRichTexts } = useSiteCore();
  const projectKeyDetails = useSelector(keyDetailsSelector);
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const {
    childProgramsTitle,
    programsSectionHeader,
    addExistingProgramsButton,
    notFinishedCreatingProjectYet,
  } = childProgramsSiteCoreKeys;
  const getLabel = useFetchLabel(childProgramsTitle);
  const [showAddExistingProgramsModal, setShowAddExistingProgramsModal] =
    useState(false);
  const navigate = useNavigate();
  const itemIdRef = React.useRef<string>("");
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const {
    isPending: isSelectedChildProgramsListPending,
    data: selectedChildProgramsListData,
    isError: isSelectedChildProgramsListError,
    refetchWithParams: getSelectedChildProgramsList,
  } = useGetSearchProgramsForProjectMutation();

  const callGetSelectedChildProgramsList = (projectId?: string) => {
    if (isNotEmpty(projectId)) {
      getSelectedChildProgramsList(projectId);
    }
  };

  useEffect(() => {
    callGetSelectedChildProgramsList(projectId);
  }, [projectId]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isSelectedChildProgramsListPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          isVisible: false,
        }),
      );
    };
  }, [isSelectedChildProgramsListPending]);

  useEffect(() => {
    if (isSelectedChildProgramsListError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSelectedChildProgramsListError]);

  const handleAddChildProgramsClick = () => {
    setShowAddExistingProgramsModal(true);
  };

  const handleCloseAddExistingProgramsModal = () => {
    setShowAddExistingProgramsModal(false);
  };

  const renderHeaderWithResultsCount = () => {
    return (
      <MuiBox className="header-with-results-count">
        <AccordionHeaderText label={getLabel(programsSectionHeader)} />
        {isNotEmpty(selectedChildProgramsListData?.length) && (
          <MuiTypography variant="h3SemiBold" className="selected-count">
            {selectedChildProgramsListData?.length}
          </MuiTypography>
        )}
      </MuiBox>
    );
  };

  const handleViewItemActionClick = (id: string) => {
    itemIdRef.current = id;
    if (!isInViewMode && !isEditProject) {
      setShowConfirmationModal(true);
    } else {
      handleViewProgramNavigation();
    }
  };

  const handleEditItemActionClick = (id: string) => {
    dispatch(startChildProgramNavigation(true));
    if (isNotEmpty(id)) {
      navigate(`${PAGE_URLS.EDIT_PROGRAM}/${id}`, {
        state: { from: pageUrl },
      });
    }
  };

  const handleViewProgramNavigation = () => {
    setShowConfirmationModal(false);
    dispatch(startChildProgramNavigation(true));
    if (isNotEmpty(itemIdRef.current)) {
      navigate(`${PAGE_URLS.VIEW_PROGRAM}/${itemIdRef.current}`, {
        state: { from: pageUrl },
      });
    }
  };

  return (
    <ChildProgramsWrapper data-testid="child-programs-main">
      <AccordionHeader
        title={renderHeaderWithResultsCount()}
        isExpanded={true}
        showExpandCollapseIcon={false}
      >
        {!isInViewMode && (
          <MuiBox className="semi-header">
            <ButtonWithIcon
              buttonText={getLabel(addExistingProgramsButton)}
              onActionClick={handleAddChildProgramsClick}
              data-testid="add-existing-programs-button"
            />
          </MuiBox>
        )}

        <ChildProgramsList
          rows={
            (selectedChildProgramsListData as ChildProgramsListRowValue[]) ??
            []
          }
          onViewItemClick={handleViewItemActionClick}
          isEditProject={isEditProject}
          onEditItemClick={handleEditItemActionClick}
          isNavigatedFromProgram={isNavigatedFromProgram}
        />

        {showAddExistingProgramsModal && (
          <AddExistingPrograms
            projectId={projectId}
            clientName={getValueOrBlank(projectKeyDetails?.client?.name)}
            clientId={getValueOrBlank(projectKeyDetails?.client?.id)}
            open={showAddExistingProgramsModal}
            onClose={handleCloseAddExistingProgramsModal}
            preSelectedChildPrograms={
              (selectedChildProgramsListData as ChildProgramsListRowValue[]) ??
              []
            }
          />
        )}
      </AccordionHeader>

      <WarningConfirmationModal
        keepMounted
        show={!isInViewMode && !isEditProject && showConfirmationModal}
        modalTitle={commonLabels.confirmation}
        primaryButtonTitle={commonLabels.yes}
        secondaryButtonTitle={commonLabels.no}
        onClose={() => setShowConfirmationModal(false)}
        onPrimaryButtonClick={handleViewProgramNavigation}
        isLabelHTMLContent={true}
        labelContent={fetchRichTexts(
          childProgramsTitle,
          notFinishedCreatingProjectYet,
        )}
      />
    </ChildProgramsWrapper>
  );
};
